import React, { useCallback, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    Field,
    Link,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    tokens,
    OnOpenChangeData,
    OpenPopoverEvents,
} from "@fluentui/react-components";
import { ArrowClockwise16Regular, ChevronDown16Regular, Folder16Regular } from "@fluentui/react-icons";
import { NewformaFolderTree, NewformaFolderTreeNode } from "../../_base/NewformaFolderTree";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        width: "100%",
    },
    field: {
        width: "100%",
        ":global(.fui-Field__label)": {
            marginBottom: tokens.spacingVerticalXS,
        },
    },
    triggerButton: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        width: "100%",
        minHeight: "32px",
        boxSizing: "border-box",
        overflow: "hidden",
        padding: "0 8px",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderBottomColor: tokens.colorNeutralStrokeAccessible,
        borderRadius: "4px",
        fontSize: "14px",
        fontFamily: tokens.fontFamilyBase,
        color: tokens.colorNeutralForeground1,
        textAlign: "left",
        cursor: "pointer",
        ":hover": {
            border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
            borderBottomColor: tokens.colorNeutralStrokeAccessible,
        },
        ":focus-visible": {
            outline: `2px solid ${tokens.colorCompoundBrandStroke}`,
        },
        "&&&:disabled": {
            backgroundColor: tokens.colorTransparentBackground,
            border: `1px solid ${tokens.colorNeutralStrokeDisabled}`,
            borderBottomColor: tokens.colorNeutralStrokeDisabled,
            color: tokens.colorNeutralForegroundDisabled,
            cursor: "not-allowed",
        },
        "&&&:disabled svg": {
            color: tokens.colorNeutralForegroundDisabled,
        },
    },
    triggerButtonDark: {
        backgroundColor: tokens.colorNeutralBackground3,
        "&&&:not(:hover):not(:focus-within):not(:disabled)": {
            "border-color": "transparent",
            "border-bottom-color": "transparent",
        },
    },
    triggerText: {
        flex: 1,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    triggerPlaceholder: {
        color: tokens.colorNeutralForeground4,
    },
    triggerFolderIcon: {
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
    },
    triggerChevron: {
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
    },
    popoverSurface: {
        padding: "8px",
        maxHeight: "260px",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
    },
    treeScrollContainer: {
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "auto",
    },
    reloadLinkRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "4px",
    },
    reloadLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "12px",
        lineHeight: "16px",
        "& svg": {
            fontSize: "13px",
        },
    },
});

export interface SubfolderPickerFluent9Props extends LocalizeContextProps {
    folders: NewformaFolderTreeNode[];
    selectedFolder?: NewformaFolderTreeNode;
    onFolderSelected: (folder: NewformaFolderTreeNode | undefined) => void;
    onReload: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    hideLabel?: boolean;
    className?: string;
    theme?: string;
}

const SubfolderPickerFluent9Inner: React.FC<SubfolderPickerFluent9Props> = (props) => {
    const styles = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [surfaceWidth, setSurfaceWidth] = useState<number | undefined>(undefined);

    const isDarkTheme = props.theme === "dark";
    const isTriggerDisabled = !!props.disabled || !!props.isLoading;

    const handleOpenChange = useCallback(
        (_event: OpenPopoverEvents, data: OnOpenChangeData) => {
            if (isTriggerDisabled) {
                setIsOpen(false);
                return;
            }
            if (data.open) {
                setSurfaceWidth(triggerRef.current?.offsetWidth);
            }
            setIsOpen(data.open);
        },
        [isTriggerDisabled]
    );

    const handleFolderSelected = useCallback(
        (folder: NewformaFolderTreeNode) => {
            props.onFolderSelected(folder);
            setIsOpen(false);
        },
        [props]
    );

    const handleReloadClick = useCallback(() => {
        if (props.isLoading || props.disabled) {
            return;
        }
        setIsOpen(false);
        props.onReload();
    }, [props]);

    const displayText = props.isLoading
        ? (props.translate("FILE_MULTIPLE_EMAIL.LOADING_SUBFOLDERS") as string)
        : props.selectedFolder?.name || (props.translate("FILE_MULTIPLE_EMAIL.SUBFOLDER_PLACEHOLDER") as string);
    const isShowingPlaceholder = props.isLoading || !props.selectedFolder;

    return (
        <div className={mergeClasses(styles.root, props.className)} data-testid="subfolder-picker">
            <Field
                className={styles.field}
                label={props.hideLabel ? undefined : (props.translate("FILE_MULTIPLE_EMAIL.SUBFOLDER_LABEL") as string)}
            >
                <Popover
                    open={isOpen && !isTriggerDisabled}
                    onOpenChange={handleOpenChange}
                    positioning={{
                        position: "below",
                        align: "start",
                        shiftToCoverTarget: true,
                    }}
                    trapFocus
                >
                    <PopoverTrigger disableButtonEnhancement>
                        <button
                            type="button"
                            ref={triggerRef}
                            className={mergeClasses(styles.triggerButton, isDarkTheme && styles.triggerButtonDark)}
                            disabled={isTriggerDisabled}
                            aria-haspopup="tree"
                            aria-expanded={isOpen}
                            data-testid="subfolder-picker-trigger"
                        >
                            <Folder16Regular aria-hidden="true" className={styles.triggerFolderIcon} />
                            <span
                                className={mergeClasses(
                                    styles.triggerText,
                                    isShowingPlaceholder && styles.triggerPlaceholder
                                )}
                            >
                                {displayText}
                            </span>
                            <ChevronDown16Regular aria-hidden="true" className={styles.triggerChevron} />
                        </button>
                    </PopoverTrigger>
                    <PopoverSurface
                        className={styles.popoverSurface}
                        style={surfaceWidth ? { width: `${surfaceWidth}px` } : undefined}
                    >
                        <div className={styles.treeScrollContainer}>
                            <NewformaFolderTree
                                folders={props.folders}
                                selectedFolderId={props.selectedFolder?.id}
                                onFolderSelected={handleFolderSelected}
                                ariaLabel={props.translate("FILE_MULTIPLE_EMAIL.SUBFOLDER_LABEL") as string}
                                dataTestId="subfolder-picker-tree"
                            />
                        </div>
                    </PopoverSurface>
                </Popover>
            </Field>
            <div className={styles.reloadLinkRow}>
                <Link
                    className={styles.reloadLink}
                    onClick={handleReloadClick}
                    disabled={!!props.isLoading || !!props.disabled}
                    data-testid="subfolder-picker-reload"
                >
                    <ArrowClockwise16Regular />
                    {props.translate("FILE_MULTIPLE_EMAIL.RELOAD_SUBFOLDERS") as string}
                </Link>
            </div>
        </div>
    );
};

export const SubfolderPickerFluent9 = withLocalize(SubfolderPickerFluent9Inner);
export default SubfolderPickerFluent9;
